import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, IconButton, CircularProgress,
  Dialog, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, InputAdornment, Divider, Alert,
  ToggleButton, ToggleButtonGroup, Tooltip, Autocomplete,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import CookieRoundedIcon from '@mui/icons-material/CookieRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import axios from 'axios';
import { Page, Card } from '../ui';

const emptyForm = () => ({ name: '', token: '', token_type: 'EAAB', proxy: '', proxy_type: 'socks5', cookies: '', user_agent: '', group: '', tags: [] });
const ROUND = { '& .MuiOutlinedInput-root': { borderRadius: '6px' } };

const UA_TEMPLATES = [
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/{V}.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/{V}.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.4 Safari/605.1.15',
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:122.0) Gecko/20100101 Firefox/122.0',
];
const genUA = () => UA_TEMPLATES[Math.floor(Math.random() * UA_TEMPLATES.length)].replace('{V}', String(118 + Math.floor(Math.random() * 12)));

function SectionLabel({ icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, mt: 0.5 }}>
      {React.cloneElement(icon, { sx: { fontSize: 16, color: 'primary.main' } })}
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</Typography>
    </Box>
  );
}

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [tagFilter, setTagFilter] = useState([]);            // выбранные теги для фильтра (любой из)
  const [tagEditor, setTagEditor] = useState(null);          // { accId, tags } — редактор тегов существующего аккаунта
  const [savingTags, setSavingTags] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get('/accounts').then((r) => setAccounts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  const loadProxies = () => { axios.get('/proxies').then((r) => setProxies(r.data)).catch(() => {}); };
  useEffect(() => { load(); loadProxies(); }, []);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const add = () => {
    if (!form.token.trim()) return;
    setAdding(true); setError('');
    axios.post('/accounts/add', {
      token: form.token.trim(),
      token_type: form.token_type,
      name: form.name.trim() || undefined,
      proxy: form.proxy.trim() || undefined,
      proxy_type: form.proxy_type,
      cookies: form.cookies.trim() || undefined,
      user_agent: form.user_agent.trim() || undefined,
      group: form.group.trim() || undefined,
      tags: form.tags,
    })
      .then(() => { setOpen(false); setForm(emptyForm()); load(); })
      .catch((e) => setError((e.response && e.response.data && e.response.data.detail) || 'Не удалось добавить'))
      .finally(() => setAdding(false));
  };

  const remove = (id) => { axios.delete(`/accounts/${id}`).then(load).catch(() => {}); };

  const saveTags = () => {
    if (!tagEditor) return;
    setSavingTags(true);
    axios.patch(`/accounts/${tagEditor.accId}/tags`, { tags: tagEditor.tags })
      .then(() => { setTagEditor(null); load(); })
      .catch(() => {})
      .finally(() => setSavingTags(false));
  };

  const groups = [...new Set(accounts.map((a) => a.group).filter(Boolean))];
  const allTags = [...new Set(accounts.flatMap((a) => a.tags || []))].sort((x, y) => x.toLowerCase().localeCompare(y.toLowerCase()));
  const shown = accounts.filter((a) =>
    (!groupFilter || (a.group || '') === groupFilter) &&
    (tagFilter.length === 0 || (a.tags || []).some((t) => tagFilter.includes(t)))
  );

  const headSx = { fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' };
  const adorn = (icon) => ({ startAdornment: <InputAdornment position="start">{React.cloneElement(icon, { sx: { fontSize: 18, color: 'text.secondary' } })}</InputAdornment> });

  return (
    <Page
      title="Аккаунты"
      subtitle="Подключение через токен EAAB: прокси, cookie и UserAgent профиля"
      action={<Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setForm(emptyForm()); setError(''); setOpen(true); }}>Добавить аккаунт</Button>}
    >
      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>Аккаунты</Typography>
            <Chip label={shown.length} size="small" color="primary" variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 150, ...ROUND }}>
              <InputLabel>Группа</InputLabel>
              <Select label="Группа" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} sx={{ borderRadius: '6px' }}>
                <MenuItem value="">Все</MenuItem>
                {groups.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </Select>
            </FormControl>
            <Autocomplete
              multiple size="small" options={allTags} value={tagFilter}
              onChange={(e, v) => setTagFilter(v)} disableCloseOnSelect
              sx={{ minWidth: 200, ...ROUND }}
              renderInput={(params) => <TextField {...params} label="Теги" placeholder={tagFilter.length ? '' : 'любой из'} />}
              renderTags={(value, getTagProps) => value.map((opt, i) => (
                <Chip label={opt} size="small" {...getTagProps({ index: i })} sx={{ height: 20, fontSize: 11 }} />
              ))}
            />
            <IconButton onClick={load} size="small" sx={{ color: 'text.secondary' }}>
              {loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
        <Table sx={{ '& td, & th': { borderColor: 'divider' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={headSx}>Название</TableCell>
              <TableCell sx={headSx}>Прокси</TableCell>
              <TableCell sx={headSx}>Группа</TableCell>
              <TableCell sx={headSx}>Теги</TableCell>
              <TableCell sx={headSx}>Токен</TableCell>
              <TableCell sx={headSx}>Статус</TableCell>
              <TableCell sx={{ ...headSx, width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 7, textAlign: 'center', border: 'none' }}>
                  <PersonRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>Аккаунтов нет</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Нажми «Добавить аккаунт».</Typography>
                </TableCell>
              </TableRow>
            ) : shown.map((acc) => (
              <TableRow key={acc.id} hover>
                <TableCell sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{acc.name || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'monospace' }}>{acc.proxy || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{acc.group ? <Chip label={acc.group} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} /> : '—'}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {(acc.tags || []).map((t) => (
                      <Chip key={t} label={t} size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: 11 }} />
                    ))}
                    <Tooltip title="Изменить теги">
                      <IconButton size="small" onClick={() => setTagEditor({ accId: acc.id, tags: [...(acc.tags || [])] })}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' }, p: 0.25 }}>
                        <LocalOfferRoundedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'monospace' }}>{acc.token ? acc.token.substring(0, 18) + '…' : '—'}</TableCell>
                <TableCell>
                  {acc.verified === false ? (
                    <Tooltip title={acc.verify_error || 'Facebook не подтвердил токен'}>
                      <Chip label="не проверен" size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: 11, fontWeight: 600 }} />
                    </Tooltip>
                  ) : (
                    <Chip label="active" size="small" color="success" variant="outlined" sx={{ height: 22, fontSize: 11, fontWeight: 600 }} />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(acc.id)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: 3, pt: 3, pb: 2 }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: '10px', flexShrink: 0,
            bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PersonAddAlt1RoundedIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'text.primary', letterSpacing: '-0.02em' }}>Новый аккаунт</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Подключение через токен EAAB</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: 'text.secondary' }}><CloseRoundedIcon /></IconButton>
        </Box>
        <Divider />

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <TextField label="Название" placeholder="Техническое имя (необязательно)" fullWidth size="small" value={form.name}
            onChange={(e) => setF('name', e.target.value)} sx={{ mb: 2.5, ...ROUND }} />

          <SectionLabel icon={<VpnKeyRoundedIcon />}>Токен доступа</SectionLabel>
          <FormControl size="small" sx={{ minWidth: 200, mb: 1.5, ...ROUND }}>
            <InputLabel>Тип токена</InputLabel>
            <Select label="Тип токена" value={form.token_type} onChange={(e) => setF('token_type', e.target.value)} sx={{ borderRadius: '6px' }}>
              <MenuItem value="EAAB">Токен EAAB</MenuItem>
              <MenuItem value="System">Токен System</MenuItem>
            </Select>
          </FormControl>
          <TextField placeholder={form.token_type === 'System' ? 'Системный токен…' : 'EAAB…'} required fullWidth size="small" multiline rows={3} value={form.token}
            onChange={(e) => setF('token', e.target.value)}
            sx={{ ...ROUND, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 12.5 } }} />
          {form.token_type === 'System' && (
            <Typography sx={{ fontSize: 11.5, color: 'warning.main', mt: 0.75 }}>
              Системный токен: часть функций (биллинг, привязка карт) может быть недоступна. Для всех фич используй EAAB.
            </Typography>
          )}
          <Box sx={{ mb: 2.5 }} />

          <Divider sx={{ mb: 2 }} />
          <SectionLabel icon={<PublicRoundedIcon />}>Окружение профиля</SectionLabel>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150, ...ROUND }}>
              <InputLabel>Из пула</InputLabel>
              <Select label="Из пула" value="" onChange={(e) => setF('proxy', e.target.value)} sx={{ borderRadius: '6px' }}>
                {proxies.length === 0 && <MenuItem value="" disabled>пул пуст</MenuItem>}
                {proxies.map((px) => <MenuItem key={px.id} value={px.raw}>{px.raw}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" placeholder="host:port:user:pass" value={form.proxy} onChange={(e) => setF('proxy', e.target.value)}
              InputProps={adorn(<PublicRoundedIcon />)}
              sx={{ flex: 1, minWidth: 220, ...ROUND, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Тип прокси:</Typography>
            <ToggleButtonGroup size="small" exclusive value={form.proxy_type} onChange={(e, v) => v && setF('proxy_type', v)}>
              <ToggleButton value="socks5" sx={{ px: 2, textTransform: 'none' }}>SOCKS5</ToggleButton>
              <ToggleButton value="http" sx={{ px: 2, textTransform: 'none' }}>HTTP</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField label="Cookie (JSON)" fullWidth size="small" multiline rows={3} value={form.cookies}
            onChange={(e) => setF('cookies', e.target.value)} InputProps={adorn(<CookieRoundedIcon />)}
            sx={{ mb: 2, ...ROUND, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 12.5 } }} />

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <TextField label="UserAgent" fullWidth size="small" value={form.user_agent} onChange={(e) => setF('user_agent', e.target.value)}
              InputProps={adorn(<DevicesRoundedIcon />)} sx={{ ...ROUND }} />
            <Button variant="outlined" startIcon={<AutorenewRoundedIcon />} onClick={() => setF('user_agent', genUA())} sx={{ whiteSpace: 'nowrap', borderRadius: '6px' }}>
              Сгенерировать
            </Button>
          </Box>

          <TextField label="Группа (необязательно)" fullWidth size="small" value={form.group} onChange={(e) => setF('group', e.target.value)}
            InputProps={adorn(<SellRoundedIcon />)} helperText="Метка для разбивки аккаунтов и фильтра в статистике" sx={{ mb: 2, ...ROUND }} />

          <Autocomplete
            multiple freeSolo size="small" options={allTags} value={form.tags}
            onChange={(e, v) => setF('tags', v.map((s) => String(s).trim()).filter(Boolean))}
            renderTags={(value, getTagProps) => value.map((opt, i) => (
              <Chip label={opt} size="small" color="primary" variant="outlined" {...getTagProps({ index: i })} sx={{ height: 20, fontSize: 11 }} />
            ))}
            renderInput={(params) => (
              <TextField {...params} label="Теги (необязательно)" placeholder="Enter для добавления"
                helperText="Несколько меток: ферма, гео, статус… Фильтр в списке и подстановка в автозаливе" />
            )}
          />

          {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: '6px' }}>{error}</Alert>}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary' }}>Закрыть</Button>
          <Button variant="contained" onClick={add} disabled={adding || !form.token.trim()} sx={{ px: 3 }}>
            {adding ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Добавить аккаунт'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!tagEditor} onClose={() => setTagEditor(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 3, pt: 2.5, pb: 1.5 }}>
          <LocalOfferRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Теги аккаунта</Typography>
        </Box>
        <Divider />
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Autocomplete
            multiple freeSolo autoFocus size="small" options={allTags} value={tagEditor ? tagEditor.tags : []}
            onChange={(e, v) => setTagEditor((p) => ({ ...p, tags: v.map((s) => String(s).trim()).filter(Boolean) }))}
            renderTags={(value, getTagProps) => value.map((opt, i) => (
              <Chip label={opt} size="small" color="primary" variant="outlined" {...getTagProps({ index: i })} sx={{ height: 22, fontSize: 12 }} />
            ))}
            renderInput={(params) => <TextField {...params} label="Теги" placeholder="Enter для добавления" autoFocus />}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTagEditor(null)} sx={{ color: 'text.secondary' }}>Отмена</Button>
          <Button variant="contained" onClick={saveTags} disabled={savingTags} sx={{ px: 3 }}>
            {savingTags ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
